import { type IdentifierRecord } from '@myplayer/schema';
import { IconEdit, IconExternalLink, IconKey, IconPlus, IconTrash } from '@myplayer/ui';
import { type ReactElement } from 'react';
import { type AdminUserSession } from './auth-context.js';
import { formatPlainValidationError } from './products-firestore.js';
import {
  ActionOverflowMenu,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardIcon,
  CardTitle,
  Input,
  ModalDialog,
  Select,
  Tag,
  useContextMenu,
} from './components/ui/primitives.js';
import { buildQualifierContextMenuItems } from './context-menu-items.js';
import {
  PRODUCT_QUALIFIER_OPTIONS,
  buildQualifierDigitalLinkPreviewUrl,
  resolveQualifierDisplayTitle,
  resolveQualifierDisplayUrl,
  resolveQualifierOption,
  resolveQualifierPrimaryPair,
  type QualifierOverrideRecord,
  type SupportedQualifierAi,
} from './product-qualifiers-helpers.js';
import { useProductQualifiers } from './use-product-qualifiers.js';
import { resolveEffectiveHost, resolveExampleBrandHostname } from './use-workspace-identifiers.js';

export interface ProductQualifiersSectionProps {
  readonly selectedRecord: IdentifierRecord;
  readonly session: AdminUserSession | null;
  readonly canEdit: boolean;
  readonly isControlled?: boolean | undefined;
  readonly initialQualifiers?: readonly QualifierOverrideRecord[] | undefined;
  readonly effectiveResolverHost?: string | undefined;
  readonly onSaved?: ((message: string) => void) | undefined;
  readonly onModalOpenChange?: ((open: boolean) => void) | undefined;
}

export function ProductQualifiersSection({
  selectedRecord,
  session,
  canEdit,
  isControlled = false,
  initialQualifiers,
  effectiveResolverHost,
  onSaved,
  onModalOpenChange,
}: ProductQualifiersSectionProps): ReactElement {
  const {
    activeRecords,
    isModalOpen,
    editingQualifierKey,
    qualifierCode,
    setQualifierCode,
    qualifierValue,
    setQualifierValue,
    qualifierUrl,
    setQualifierUrl,
    qualifierLabel,
    setQualifierLabel,
    formError,
    saving,
    handleOpenAddModal,
    handleOpenEditModal,
    handleCloseModal,
    handleSaveQualifier,
    handleRemoveQualifier,
  } = useProductQualifiers({
    selectedRecord,
    session,
    isControlled,
    initialQualifiers,
    onSaved,
    onModalOpenChange,
  });

  const resolvedHost = resolveEffectiveHost(effectiveResolverHost);
  const hostForPlaceholder = resolveExampleBrandHostname(effectiveResolverHost);
  const selectedOption = resolveQualifierOption(qualifierCode);
  const { bindContextMenu, contextMenuElement } = useContextMenu();

  return (
    <>
      <Card data-testid="product-qualifiers-card">
        <CardHeader>
          <div className="flex min-w-0 items-center gap-3">
            <CardIcon>
              <IconKey />
            </CardIcon>
            <div className="min-w-0">
              <CardTitle>Qualifiers ({activeRecords.length})</CardTitle>
              <CardDescription>
                Batch/lot (10), serial (21) and CPV (22) overrides with their own link
              </CardDescription>
            </div>
          </div>
          {canEdit && (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              data-testid="add-qualifier-button"
              onClick={handleOpenAddModal}
            >
              <IconPlus />
              <span>Add qualifier</span>
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {activeRecords.length === 0 ? (
            <p data-testid="qualifiers-empty-state" className="py-2 text-[12px] text-fg-secondary">
              No qualifier overrides configured. Scans with batch/lot (10), serial (21), or CPV (22)
              use the main product link.
            </p>
          ) : (
            <ul
              aria-label="Qualifier overrides list"
              data-testid="qualifiers-list"
              className="divide-y divide-subtle border-y border-subtle"
            >
              {activeRecords.map((rec) => {
                const pair = resolveQualifierPrimaryPair(rec);
                const opt = resolveQualifierOption(pair.code);
                const displayUrl = resolveQualifierDisplayUrl(rec);
                const displayTitle = resolveQualifierDisplayTitle(rec);
                const defaultLabel = `${opt.shortLabel} ${pair.value}`.trim();
                const previewUri = buildQualifierDigitalLinkPreviewUrl({
                  host: resolvedHost,
                  parentAi: selectedRecord.ai,
                  parentPrimaryKey: selectedRecord.primaryKey,
                  qualifierCode: pair.code,
                  qualifierValue: pair.value,
                });

                return (
                  <li
                    key={rec.qualifierKey}
                    data-testid={`qualifier-row-${rec.qualifierKey}`}
                    onContextMenu={bindContextMenu(() =>
                      buildQualifierContextMenuItems({
                        record: rec,
                        parentAi: selectedRecord.ai,
                        parentPrimaryKey: selectedRecord.primaryKey,
                        resolvedHost,
                        canEdit,
                        saving,
                        onEdit: handleOpenEditModal,
                        onRemove: handleRemoveQualifier,
                      }),
                    )}
                    className="flex flex-wrap items-center justify-between gap-3 bg-surface py-3 text-[12px]"
                  >
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <Tag variant="outline">{opt.shortLabel}</Tag>
                        <span className="font-mono font-semibold text-fg">{pair.value}</span>
                        {displayTitle !== defaultLabel && (
                          <span className="font-semibold text-fg">{displayTitle}</span>
                        )}
                      </div>
                      <div className="flex min-w-0 flex-wrap items-center gap-2 font-mono text-fg-secondary">
                        {/^https?:\/\//i.test(displayUrl) ? (
                          <a
                            href={displayUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`qualifier-target-url-${rec.qualifierKey}`}
                            className="inline-flex min-w-0 items-center gap-1.5 truncate underline decoration-subtle underline-offset-2 hover:text-brand"
                          >
                            <IconExternalLink className="shrink-0" />
                            <span className="truncate">{displayUrl}</span>
                          </a>
                        ) : (
                          <span
                            data-testid={`qualifier-target-url-${rec.qualifierKey}`}
                            className="truncate"
                          >
                            {displayUrl}
                          </span>
                        )}
                        {previewUri.length > 0 && (
                          <span className="truncate text-[11px] text-fg-muted">({previewUri})</span>
                        )}
                      </div>
                      {rec.validation?.status === 'INVALID' && rec.validation.errors.length > 0 && (
                        <p
                          role="alert"
                          data-testid={`qualifier-validation-error-${rec.qualifierKey}`}
                          className="text-[11px] font-medium text-status-error"
                        >
                          {formatPlainValidationError(
                            rec.validation.errors[0] ?? {
                              code: 'INVALID',
                              message: 'Invalid qualifier link',
                            },
                          )}
                        </p>
                      )}
                    </div>

                    {canEdit && (
                      <div className="flex items-center gap-1.5">
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          data-testid={`edit-qualifier-button-${rec.qualifierKey}`}
                          aria-label={`Edit qualifier ${opt.shortLabel} ${pair.value}`}
                          disabled={saving}
                          onClick={() => handleOpenEditModal(rec)}
                        >
                          <IconEdit />
                          <span>Edit</span>
                        </Button>
                        <ActionOverflowMenu
                          label={`More actions for ${opt.shortLabel} ${pair.value}`}
                          disabled={saving}
                        >
                          <Button
                            type="button"
                            size="sm"
                            variant="destructive"
                            data-testid={`remove-qualifier-button-${rec.qualifierKey}`}
                            aria-label={`Remove qualifier ${opt.shortLabel} ${pair.value}`}
                            disabled={saving}
                            onClick={() => void handleRemoveQualifier(rec)}
                          >
                            <IconTrash />
                            <span>Remove</span>
                          </Button>
                        </ActionOverflowMenu>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
      {contextMenuElement}

      <ModalDialog
        open={isModalOpen}
        presentation="center"
        title={editingQualifierKey !== null ? 'Edit qualifier override' : 'Add qualifier override'}
        subtitle="Route a specific batch/lot (10), serial (21), or CPV (22) to its own link"
        icon={<IconKey />}
        maxWidthClassName="max-w-2xl"
        submitting={saving}
        onClose={handleCloseModal}
        data-testid="qualifier-modal"
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              data-testid="cancel-qualifier-button"
              disabled={saving}
              onClick={handleCloseModal}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="product-qualifier-editor-form"
              variant="primary"
              size="sm"
              data-testid="save-qualifier-button"
              disabled={saving}
            >
              <span>Save</span>
            </Button>
          </>
        }
      >
        <form
          id="product-qualifier-editor-form"
          onSubmit={(e) => void handleSaveQualifier(e)}
          className="space-y-4"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="qualifier-type-select"
                className="mb-1.5 block text-[12px] font-medium text-fg-secondary"
              >
                Qualifier type
              </label>
              <Select
                id="qualifier-type-select"
                data-testid="qualifier-type-select"
                value={qualifierCode}
                onChange={(e) => setQualifierCode(e.target.value as SupportedQualifierAi)}
              >
                {PRODUCT_QUALIFIER_OPTIONS.map((opt) => (
                  <option key={opt.code} value={opt.code}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <label
                htmlFor="qualifier-value-input"
                className="mb-1.5 block text-[12px] font-medium text-fg-secondary"
              >
                Qualifier value
              </label>
              <Input
                id="qualifier-value-input"
                data-testid="qualifier-value-input"
                value={qualifierValue}
                onChange={(e) => setQualifierValue(e.target.value)}
                placeholder={selectedOption.placeholder}
              />
            </div>

            <div className="sm:col-span-2">
              <label
                htmlFor="qualifier-url-input"
                className="mb-1.5 block text-[12px] font-medium text-fg-secondary"
              >
                Where should scanning go? (HTTPS)
              </label>
              <Input
                id="qualifier-url-input"
                data-testid="qualifier-url-input"
                value={qualifierUrl}
                onChange={(e) => setQualifierUrl(e.target.value)}
                placeholder={`https://${hostForPlaceholder}/batches/${selectedOption.placeholder.toLowerCase()}`}
              />
            </div>

            <div className="sm:col-span-2">
              <label
                htmlFor="qualifier-label-input"
                className="mb-1.5 block text-[12px] font-medium text-fg-secondary"
              >
                Label (optional)
              </label>
              <Input
                id="qualifier-label-input"
                data-testid="qualifier-label-input"
                value={qualifierLabel}
                onChange={(e) => setQualifierLabel(e.target.value)}
                placeholder="Autumn 2026 Roastery Batch"
              />
            </div>
          </div>

          {formError !== null && (
            <p
              role="alert"
              data-testid="qualifier-editor-error"
              className="rounded-control border border-status-error-border bg-status-error-bg p-2.5 text-[12px] font-medium text-status-error"
            >
              {formError}
            </p>
          )}
        </form>
      </ModalDialog>
    </>
  );
}
